/**
 * Draft helper utilities
 * Common functions used across draft components
 */
import { formatBattingAvg as sharedFormatBattingAvg, formatERA as sharedFormatERA } from '../../../../services/statFormatUtils';
import { getNestedValue } from '../../../../utils/getNestedValue';
import { isPitcherPosition } from '../../../../utils/playerClassification';

export { getNestedValue };

/**
 * Get default position eligibility for a player position
 * @param {string} position - Player's primary position
 * @returns {string[]} Array of eligible roster slots
 */
export const getDefaultEligibility = (position) => {
  const POSITION_ELIGIBILITY = {
    "C": ["C", "UTIL"],
    "1B": ["1B", "CI", "UTIL"],
    "2B": ["2B", "MI", "UTIL"],
    "3B": ["3B", "CI", "UTIL"],
    "SS": ["SS", "MI", "UTIL"],
    "LF": ["OF", "UTIL"],
    "CF": ["OF", "UTIL"],
    "RF": ["OF", "UTIL"],
    "OF": ["OF", "UTIL"],
    "DH": ["UTIL"],
    "SP": ["P"],
    "RP": ["P"],
    "P": ["P"]
  };
  return POSITION_ELIGIBILITY[position] || [];
};

/**
 * Check if a player is a pitcher
 * @param {string} position - Player's position
 * @returns {boolean}
 */
export const isPitcher = (position) => {
  return isPitcherPosition(position);
};

/**
 * Check if a player is a hitter
 * @param {string} position - Player's position
 * @returns {boolean}
 */
export const isHitter = (position) => {
  return !isPitcher(position);
};

/**
 * Format time remaining as MM:SS
 * @param {number} seconds - Time remaining in seconds
 * @returns {string} Formatted time string
 */
export const formatTimeRemaining = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Calculate round number from overall pick number
 * @param {number} pickNumber - Overall pick number (1-indexed)
 * @param {number} teamsCount - Number of teams in the draft
 * @returns {number} Round number
 */
export const calculateRound = (pickNumber, teamsCount) => {
  if (!pickNumber || !teamsCount) return 0;
  return Math.ceil(pickNumber / teamsCount);
};

/**
 * Calculate pick within round from overall pick number
 * @param {number} pickNumber - Overall pick number (1-indexed)
 * @param {number} teamsCount - Number of teams in the draft
 * @returns {number} Pick number within round
 */
export const calculatePickInRound = (pickNumber, teamsCount) => {
  if (!pickNumber || !teamsCount) return 0;
  const remainder = pickNumber % teamsCount;
  return remainder === 0 ? teamsCount : remainder;
};

/**
 * Format batting average (removes leading zero)
 * @param {number} avg - Batting average
 * @returns {string} Formatted average (e.g., ".321")
 */
export const formatBattingAvg = (avg) => sharedFormatBattingAvg(avg);

/**
 * Format ERA to 2 decimal places
 * @param {number} value - Stat value
 * @returns {string} Formatted value
 */
export const formatPitchingStat = (value) => sharedFormatERA(value);

/**
 * Sort players by a given key
 * @param {Object[]} players - Array of players
 * @param {string} key - Sort key (dot notation supported)
 * @param {string} direction - 'asc' or 'desc'
 * @returns {Object[]} Sorted array
 */
export const sortPlayers = (players, key, direction = 'desc') => {
  return [...players].sort((a, b) => {
    const aValue = getNestedValue(a, key);
    const bValue = getNestedValue(b, key);

    // Handle nulls (sort to end)
    if (aValue === bValue) return 0;
    if (aValue == null) return direction === 'asc' ? 1 : -1;
    if (bValue == null) return direction === 'asc' ? -1 : 1;

    // Numeric comparison
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return direction === 'asc' ? aValue - bValue : bValue - aValue;
    }

    // Try parsing as numbers
    const aNum = parseFloat(aValue);
    const bNum = parseFloat(bValue);
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return direction === 'asc' ? aNum - bNum : bNum - aNum;
    }

    // String comparison
    const aStr = String(aValue).toLowerCase();
    const bStr = String(bValue).toLowerCase();
    const result = aStr.localeCompare(bStr);
    return direction === 'asc' ? result : -result;
  });
};

/**
 * Filter players based on filter criteria
 * @param {Object[]} players - Array of players
 * @param {Object} filters - Filter criteria
 * @param {Set} draftedIds - Set of already drafted player IDs
 * @returns {Object[]} Filtered array
 */
export const filterPlayers = (players, filters, draftedIds = new Set()) => {
  if (!players) return [];

  let result = [...players];

  // Filter out drafted players
  if (draftedIds.size > 0) {
    result = result.filter(p => !draftedIds.has(p.ids?.league_player));
  }

  // Apply position filter
  if (filters.position && filters.position !== 'ALL') {
    result = result.filter(p => p.info?.position === filters.position);
  }

  // Apply team filter
  if (filters.team && filters.team !== 'ALL') {
    result = result.filter(p => p.info?.mlb_team === filters.team);
  }

  // Apply search
  if (filters.search) {
    const term = filters.search.toLowerCase();
    result = result.filter(p =>
      p.info?.full_name?.toLowerCase().includes(term)
    );
  }

  // Apply sort
  if (filters.sortBy) {
    result = sortPlayers(result, filters.sortBy, filters.sortOrder || 'desc');
  }

  return result;
};

/**
 * Transform player for position assignment modal
 * Adds effective_eligibility based on position
 * @param {Object} player - Player object
 * @returns {Object} Transformed player with eligibility
 */
/**
 * Download draft results as CSV
 * @param {Object[]} picks - Array of draft pick objects
 * @param {string} leagueName - League name for filename
 * @param {number} year - Draft year
 */
export const downloadDraftCSV = (picks, leagueName = 'League', year = new Date().getFullYear()) => {
  if (!picks || picks.length === 0) return;

  const headers = [
    'Overall Pick', 'Round', 'Pick in Round', 'Team',
    'Player', 'Position', 'MLB Team', 'Salary', 'Contract Years'
  ];

  const rows = picks.map(pick => [
    pick.overall_pick_number,
    pick.round,
    pick.pick_in_round,
    pick.current_owner?.team_name || '',
    pick.player?.info?.full_name || '(Skipped)',
    pick.player?.info?.position || '',
    pick.player?.info?.mlb_team || '',
    pick.salary != null ? parseFloat(pick.salary).toFixed(2) : '',
    pick.contract_years || ''
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${leagueName.replace(/[^a-zA-Z0-9]/g, '_')}_Draft_${year}.csv`;
  link.click();
  URL.revokeObjectURL(url);
};

export const transformPlayerForAssignment = (player) => {
  if (!player) return null;

  const playerPosition = player.info?.position;
  const eligibility = getDefaultEligibility(playerPosition);

  return {
    ...player,
    info: {
      first_name: player.info?.first_name,
      last_name: player.info?.last_name,
      full_name: player.info?.full_name,
      position: playerPosition,
      mlb_team: player.info?.mlb_team
    },
    league_context: {
      roster: {
        status: null,
        position: null,
        effective_eligibility: eligibility,
        default_eligibility: eligibility,
        custom_eligibility: null
      }
    }
  };
};
